import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../LanguageContext';
import { RateCardView } from './RateCard';

const compact = (value) => {
    const n = Number(value || 0);
    if (n >= 1000000) return `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
    return `${n}`;
};

/**
 * The media kit and rate card, folded into an applicant row.
 *
 * This is what "automatically attached" means in practice: the brand never
 * asks for a PDF and the creator never sends one — the numbers are simply
 * there, next to the price being proposed.
 *
 * Renders nothing when the creator has published neither, so an application
 * from someone who has not filled anything in still looks like a plain row
 * rather than a row full of zeroes.
 */
export default function ApplicantKit({ mediaKit, rateCard, username }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    if (!mediaKit && !rateCard) return null;

    const hasNumbers = mediaKit && (mediaKit.avg_reach > 0 || mediaKit.engagement_rate > 0);

    return (
        <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">

            {/* The headline row — readable without expanding anything */}
            <button type="button" onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-100 transition">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>

                <div className="flex items-center gap-2.5 flex-wrap flex-1 min-w-0">
                    {hasNumbers && mediaKit.avg_reach > 0 && (
                        <span className="text-[11px] text-slate-600">
                            <strong className="text-slate-800 tabular-nums">{compact(mediaKit.avg_reach)}</strong> {t('mediaKit.reach')}
                        </span>
                    )}
                    {hasNumbers && mediaKit.engagement_rate > 0 && (
                        <span className="text-[11px] text-slate-600">
                            <strong className="text-primary-700 tabular-nums">%{mediaKit.engagement_rate}</strong> {t('mediaKit.engagementShort')}
                        </span>
                    )}
                    {rateCard?.starting_usd > 0 && (
                        <span className="text-[11px] text-slate-600">
                            {t('rateCard.from')} <strong className="text-slate-800 tabular-nums">${rateCard.starting_usd.toLocaleString('en-US')}</strong>
                        </span>
                    )}
                    {!hasNumbers && !rateCard?.starting_usd && (
                        <span className="text-[11px] text-slate-500">{t('mediaKit.attachedShort')}</span>
                    )}
                </div>

                <svg xmlns="http://www.w3.org/2000/svg"
                    className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open && (
                <div className="px-3 pb-3 space-y-3 border-t border-slate-200 pt-2.5 bg-white">
                    {mediaKit?.headline && (
                        <p className="text-xs font-semibold text-slate-700 leading-snug">{mediaKit.headline}</p>
                    )}

                    {/* Per-platform figures */}
                    {mediaKit?.platforms?.length > 0 && (
                        <div className="space-y-1">
                            {mediaKit.platforms.map(platform => (
                                <div key={platform.id} className="flex items-center gap-2 text-[11px]">
                                    <span className="font-bold text-slate-700 capitalize w-20 flex-shrink-0 truncate">
                                        {platform.social_media}
                                    </span>
                                    <span className="text-slate-500 tabular-nums">
                                        {compact(platform.followers)} {t('profile.followers')}
                                    </span>
                                    {platform.avg_reach > 0 && (
                                        <span className="text-slate-500 tabular-nums">
                                            · {compact(platform.avg_reach)} {t('mediaKit.reach')}
                                        </span>
                                    )}
                                    {platform.engagement_rate > 0 && (
                                        <span className="text-primary-700 font-bold tabular-nums ml-auto">
                                            %{platform.engagement_rate}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Audience, just the top slices */}
                    {mediaKit?.top_countries?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {mediaKit.top_countries.map((row, index) => (
                                <span key={index} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold">
                                    {row.label} %{row.percent}
                                </span>
                            ))}
                            {mediaKit.top_age?.map((row, index) => (
                                <span key={`age-${index}`} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold">
                                    {row.label} %{row.percent}
                                </span>
                            ))}
                        </div>
                    )}

                    {rateCard && (
                        <div>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                                {t('rateCard.title')}
                            </p>
                            <RateCardView card={rateCard} compact />
                        </div>
                    )}

                    <div className="flex items-center gap-2 pt-0.5">
                        <Link to={`/${username}/media-kit`} target="_blank" rel="noopener noreferrer"
                            className="btn-secondary py-1.5 px-3 text-[11px] no-underline">
                            {t('mediaKit.openFullPage')}
                        </Link>
                        {mediaKit?.self_reported && (
                            <span className="text-[10px] text-slate-400 leading-tight">
                                {t('mediaKit.selfReportedShort')}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
